import {
  type ECategoryMap,
  type ESceneMap,
  type ESelected,
  type GeneralID,
  type Preset,
  type ST,
  type SceneData,
  TemplateId,
  type TourData,
} from '@types';
import { combine, createApi, createDomain } from 'effector';
import produce, { type Draft } from 'immer';

const domain = createDomain('data');

export const $tourData = domain.store<Partial<TourData>>({});
export const $tourDataApi = createApi($tourData, {
  set: (state, data: TourData) => data,
  update: (state, updater: (draft: Draft<TourData>) => TourData | void) => {
    return produce(state, updater);
  },
  updateSandtable: (
    state,
    payload: {
      id: string;
      updater: (draft: Draft<ST>) => ST | void;
    }
  ) => {
    return produce(state, (draft) => {
      const sandTable = draft.sandBox?.find((x) => x.id === payload.id);
      if (sandTable) payload.updater(sandTable);
    });
  },
});

/** 存储第一次加载时的数据, 用于对比 */
export const $origTourData = domain.store<Partial<TourData>>({});
export const $origTourDataApi = createApi($origTourData, {
  set: (state, data: TourData) => data,
});

export const $categoryMap = domain.store<ECategoryMap>(new Map());
export const $categoryMapApi = createApi($categoryMap, {
  init: (store, payload: ECategoryMap) => payload,
  update: (
    state,
    updater: (draft: Draft<ECategoryMap>) => ECategoryMap | void
  ) => {
    return produce(state, updater);
  },
});

export const $categoryList = domain.store<number[]>([]);
export const $categoryListApi = createApi($categoryList, {
  init: (store, payload: number[]) => payload,
  update: (state, updater: (draft: Draft<number[]>) => number[] | void) => {
    return produce(state, updater);
  },
});

export const $largestCategoryId = domain.store<number>(1);
export const $largestCategoryIdApi = createApi($largestCategoryId, {
  set: (s, payload: number) => payload,
});

export const $sceneMap = domain.store<ESceneMap>(new Map());
export const $sceneMapApi = createApi($sceneMap, {
  init: (store, payload: ESceneMap) => payload,
  update: (state, updater: (draft: Draft<ESceneMap>) => ESceneMap | void) => {
    return produce(state, updater);
  },
  updateScene: (
    state,
    payload: {
      id: GeneralID;
      updater: (draft: Draft<SceneData>) => SceneData | void;
    }
  ) => {
    return produce(state, (draft) => {
      const scene = draft.get(payload.id);
      if (scene) payload.updater(scene);
    });
  },
});

/** 存储第一次加载时的数据, 用于对比 */
export const $origSceneMap = domain.store<ESceneMap>(new Map());
export const $origSceneMapApi = createApi($origSceneMap, {
  init: (store, payload: ESceneMap) => payload,
});

export const $selectedPath = domain.store<ESelected>({
  id: null,
  categoryPath: [],
});
export const $selectedPathApi = createApi($selectedPath, {
  update: (state, updater: (draft: Draft<ESelected>) => ESelected | void) => {
    return produce(state, updater);
  },
});

export const $curSceneId = $selectedPath.map((s) => s.id);
export const $curCategoryId = $selectedPath.map((s) => s.categoryPath[0]);
export const $curSubCategoryId = $selectedPath.map((s) => s.categoryPath[1]);
export const $curSceneParentId = $selectedPath.map(
  (s) => s.categoryPath[s.categoryPath.length - 1]
);

export const $subCategoryList = combine(
  $categoryMap,
  $selectedPath,
  (map, { categoryPath }) => {
    const id = categoryPath[0];
    return map.get(id)?.categoryIds || [];
  }
);
export const $sceneList = combine(
  $categoryMap,
  $selectedPath,
  (map, { categoryPath }) => {
    const id = categoryPath[categoryPath.length - 1];
    return map.get(id)?.sceneIds || [];
  }
);

export const $curScene = combine($sceneMap, $curSceneId, (map, id) => {
  if (!id) return null;
  return map.get(id) ?? null;
});

export const $origSceneIds = domain.store<GeneralID[]>([]);
export const $origSceneIdsApi = createApi($origSceneIds, {
  set: (_, payload: GeneralID[]) => payload,
});

export const $batchData = domain.store<Partial<TourData>>({});
export const $batchDataApi = createApi($batchData, {
  set: (state, data: Partial<TourData>) => data,
  update: (
    state,
    updater: (draft: Draft<Partial<TourData>>) => Partial<TourData> | void
  ) => {
    return produce(state, updater);
  },
  clear: () => ({}),
});

export const $presetEditing = domain.store<Preset>({});
export const $presetEditingApi = createApi($presetEditing, {
  set: (state, data: Preset) => data,
  update: (state, updater: (draft: Draft<Preset>) => Preset | void) => {
    return produce(state, updater);
  },
  clear: (state) => ({ templateId: state.templateId }),
});

export const $templateId = combine(
  $tourData.map((s) => s.templateId),
  $batchData.map((s) => s.templateId),
  $presetEditing.map((s) => s.templateId),
  (editId, batchId, presetId) => {
    if (editId) return editId;
    if (batchId) return batchId;
    if (presetId) return presetId;
    return TemplateId.vip;
  }
);

export const $sandTable = $tourData.map((s) => s.sandBox || []);

export const $curSandTableId = domain.store<string | null>(null);
export const $curSandTableIdApi = createApi($curSandTableId, {
  init: (state, id: string) => state || id,
  set: (_, id: string | null) => id,
  unset: () => null,
});

export const $curSandTable = combine(
  $sandTable,
  $curSandTableId,
  (list, id) => {
    const data = list.find((x) => x.id === id);
    if (!data) return null;
    return data;
  }
);
